import { useState } from 'react';
import { useClassPerfTrends, useStudentPatterns, useStudentWeeklyTrend } from '../../../api/hooks';
import type { ScoreQuartiles } from '../../../api/types';
import { EmptyState } from '../../../components/ui';
import { FaChartLine } from '../../../components/ui/icons';
import MultiLineTrendChart from '../../../components/charts/MultiLineTrendChart';
import Box from '../components/Box';
import StudentTrendChart from '../components/StudentTrendChart';
import { mean } from '../lib/mastery';
import { dayMonth } from '../lib/format';
import { FAILED, LOADING, PickSubject, SubjectPills, type SubjectKey, pct, subjectLabel, useStudentSubjects } from './shared';

const CONSISTENCY = {
  steady: { label: 'Steady', note: 'Scores stay close together from exam to exam.' },
  variable: { label: 'Variable', note: 'Scores move around a fair amount between exams.' },
  up_and_down: { label: 'Up and down', note: 'Scores swing widely: worth asking what differs between good and bad exams.' },
} as const;

/** A box plot row: the middle half as a bar, the median as a tick, min-max as a line. */
function SpreadRow({ label, q, tone }: { label: string; q: ScoreQuartiles; tone: string }) {
  const at = (v: number) => `${Math.max(0, Math.min(100, v))}%`;
  return (
    <div>
      <div className="mb-1.5 flex justify-between gap-2 text-[13px]">
        <span className="font-semibold text-ink">{label}</span>
        <span className="font-mono text-ink">median {q.median.toFixed(0)}%<span className="text-muted"> · middle half {q.p25.toFixed(0)}–{q.p75.toFixed(0)}%</span></span>
      </div>
      <div role="img" aria-label={`${label}: lowest ${q.min.toFixed(0)}%, middle half ${q.p25.toFixed(0)} to ${q.p75.toFixed(0)}%, median ${q.median.toFixed(0)}%, highest ${q.max.toFixed(0)}%`}
        className="relative h-6 rounded-full bg-track">
        <span className="absolute top-1/2 h-[2px] -translate-y-1/2 bg-line-hover" style={{ left: at(q.min), width: `calc(${at(q.max)} - ${at(q.min)})` }} />
        <span className={`absolute top-1 h-4 rounded-[4px] ${tone}`} style={{ left: at(q.p25), width: `calc(${at(q.p75)} - ${at(q.p25)})` }} />
        <span className="absolute top-0 h-6 w-[3px] rounded-sm bg-navy dark:bg-ink" style={{ left: `calc(${at(q.median)} - 1.5px)` }} />
      </div>
    </div>
  );
}

export default function ProgressView({ id, studentName, cohort }: {
  id: number; studentName: string; cohort?: { gradeId: number | null; sectionId: number | null };
}) {
  const [subject, setSubject] = useState<SubjectKey>('all');
  const subjects = useStudentSubjects(id);
  const subjectName = subjectLabel(subjects, subject);
  const trendQ = useStudentWeeklyTrend(id, { subject: subject === 'all' ? undefined : subject });
  const classTrendQ = useClassPerfTrends({ grade: cohort?.gradeId ?? null, section: cohort?.sectionId ?? 'all', subject });
  const patternsQ = useStudentPatterns(id, subject);

  const weeks = trendQ.data?.overall ?? [];
  const studentSeries = weeks.map((w) => w.avg_score);
  const classByWeek = new Map((classTrendQ.data?.series ?? []).map((p) => [p.week_start, p.avg_score]));
  const classSeries = classTrendQ.data ? weeks.map((w) => classByWeek.get(w.week_start) ?? null) : null;
  const weeklyAvg = mean(studentSeries);
  const classAvg = classSeries ? mean(classSeries) : null;
  const subline = weeklyAvg === null ? undefined
    : `Last ${weeks.length} ${weeks.length === 1 ? 'week' : 'weeks'} · ${studentName} ${weeklyAvg.toFixed(1)}%${classAvg !== null ? ` vs class ${classAvg.toFixed(1)}%` : ''}`;
  const p = patternsQ.data;
  const ranks = (p?.ranks ?? []).filter((r) => r.rank != null);

  return (
    <div className="space-y-4">
      <SubjectPills id={id} value={subject} onChange={setSubject} note="Filters every block here" />

      <Box title={`Weekly average · ${subjectName}`} help="weekly_trend" subtitle={subline}
        actions={weeks.length > 0 ? (
          <span className="flex flex-wrap gap-3.5 text-xs text-muted">
            <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="w-4 border-t-[3px] border-accent" />{studentName}</span>
            {classSeries && <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="w-4 border-t-2 border-dashed border-slate" />Class average</span>}
            <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="w-4 border-t-[1.5px] border-dashed border-accent-ink" />Pass mark</span>
          </span>
        ) : undefined}>
        {trendQ.isLoading ? LOADING : trendQ.isError || !trendQ.data ? FAILED :
         weeks.length === 0 ? <EmptyState compact icon={<FaChartLine />} title="No exam attempts yet" /> :
         <StudentTrendChart weeks={weeks.map((w) => w.week_start)} student={studentSeries} classAvg={classSeries}
           ariaLabel={`${subjectName}: ${studentName}'s weekly average from ${Math.round(studentSeries.find((v) => v != null) ?? 0)}% to ${Math.round([...studentSeries].reverse().find((v) => v != null) ?? 0)}%`} />}
      </Box>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Box title="Score spread" help="The student's exam scores against the class's on the same exams: the middle half of the scores as a bar, the median as a tick.">
          {patternsQ.isLoading ? LOADING : patternsQ.isError || !p ? FAILED :
           !p.spread.student ? <p className="text-sm text-muted">No exam results yet.</p> : (
             <div className="space-y-4">
               <SpreadRow label={studentName} q={p.spread.student} tone="bg-accent/70" />
               {p.spread.class && <SpreadRow label="Class" q={p.spread.class} tone="bg-slate/50" />}
               {p.spread.consistency && (
                 <p className="text-[13px] text-muted-2"><span className="font-semibold text-ink">{CONSISTENCY[p.spread.consistency].label}.</span> {CONSISTENCY[p.spread.consistency].note}</p>
               )}
             </div>
           )}
        </Box>
        <Box title="Rank movement" help="Where the student placed in the class on each exam, oldest first.">
          {patternsQ.isLoading ? LOADING : patternsQ.isError || !p ? FAILED :
           ranks.length === 0 ? <p className="text-sm text-muted">No ranked exams yet.</p> : (
             <ol className="space-y-2">
               {ranks.slice(-8).map((r, i, arr) => {
                 const prev = i > 0 ? arr[i - 1] : null;
                 const moved = prev && prev.percentile != null && r.percentile != null ? r.percentile - prev.percentile : null;
                 return (
                   <li key={`${r.exam}-${r.date}`} className="flex items-center gap-3 text-[13px]">
                     <span className="w-14 flex-none font-mono text-xs text-muted">{dayMonth(r.date)}</span>
                     <span className="min-w-0 flex-1 truncate text-ink">{r.exam}</span>
                     <span className="whitespace-nowrap font-mono text-ink">#{r.rank} of {r.of}</span>
                     <span className={`w-14 text-right font-mono text-xs ${moved == null ? 'text-muted' : moved >= 0 ? 'text-success' : 'text-danger'}`}>
                       {moved == null ? '' : `${moved >= 0 ? '↑' : '↓'} ${Math.abs(moved)}`}
                     </span>
                   </li>
                 );
               })}
             </ol>
           )}
          {ranks.length > 0 && <p className="mt-3 text-xs text-muted">Arrows: change in the share of the class they were ahead of ({pct(ranks[ranks.length - 1].percentile)} on the latest).</p>}
        </Box>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Box title="Trend by subject" help="Weekly average score, one line per subject.">
          {trendQ.isLoading ? LOADING : trendQ.isError || !trendQ.data ? FAILED :
           <MultiLineTrendChart series={trendQ.data.by_subject.map((s) => ({ name: s.subject_name, data: s.series.map((x) => ({ x: x.week_start, y: x.avg_score })) }))} />}
        </Box>
        <Box title="Trend by chapter" help="Weekly average score, one line per chapter of the chosen subject.">
          {subject === 'all' ? <PickSubject what="per-chapter trends" /> :
           trendQ.isLoading ? LOADING : trendQ.isError || !trendQ.data ? FAILED :
           <MultiLineTrendChart series={trendQ.data.by_chapter.map((c) => ({ name: c.chapter_name, data: c.series.map((x) => ({ x: x.week_start, y: x.avg_score })) }))} />}
        </Box>
      </div>
    </div>
  );
}
